import { useEffect, useRef } from 'react';
import type { AnalyticsConfig } from '@/utils/analyticsScriptBuilder';

/**
 * Hook to inject third-party analytics scripts into document.head on the client side.
 *
 * - Skips injection when editMode is true
 * - Checks if scripts already exist (from SSR) to avoid duplicates
 * - Cleans up injected scripts on unmount
 */
export function useThirdPartyAnalytics(
  analyticsConfig: AnalyticsConfig | null | undefined,
  editMode: boolean
) {
  const injectedElements = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (editMode || !analyticsConfig || typeof document === 'undefined') return;

    const elements: HTMLElement[] = [];

    // Helper to create and append a script element
    const addScript = (attrs: Record<string, string>, content?: string) => {
      const script = document.createElement('script');
      Object.entries(attrs).forEach(([key, value]) => script.setAttribute(key, value));
      if (content) script.textContent = content;
      document.head.appendChild(script);
      elements.push(script);
    };

    // Helper to add a noscript element to body
    const addNoscriptToBody = (innerHTML: string) => {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = innerHTML;
      document.body.insertBefore(noscript, document.body.firstChild);
      elements.push(noscript);
    };

    // Validation patterns (same as analyticsScriptBuilder.ts)
    const patterns: Record<string, RegExp> = {
      ga4: /^G-[A-Z0-9]{4,15}$/,
      gtm: /^GTM-[A-Z0-9]{4,10}$/,
      meta: /^\d{10,20}$/,
      clarity: /^[a-z0-9]{6,20}$/i,
      hotjar: /^\d{4,12}$/,
    };

    // 1. GTM
    const gtmId = analyticsConfig.gtm_container_id?.trim();
    if (gtmId && patterns.gtm.test(gtmId)) {
      // Check if GTM is already loaded (from SSR)
      if (!document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`)) {
        // dataLayer init + GTM loader
        addScript({}, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`);
        addNoscriptToBody(`<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`);
      }
    }

    // 2. GA4
    const ga4Id = analyticsConfig.ga4_measurement_id?.trim();
    if (ga4Id && patterns.ga4.test(ga4Id)) {
      if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${ga4Id}"]`)) {
        addScript({ async: '', src: `https://www.googletagmanager.com/gtag/js?id=${ga4Id}` });
        addScript({}, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga4Id}');`);
      }
    }

    // 3. Meta Pixel
    const metaId = analyticsConfig.meta_pixel_id?.trim();
    if (metaId && patterns.meta.test(metaId)) {
      if (!(window as any).fbq) {
        addScript({}, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${metaId}');fbq('track','PageView');`);
        addNoscriptToBody(`<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${metaId}&ev=PageView&noscript=1"/>`);
      }
    }

    // 4. Microsoft Clarity
    const clarityId = analyticsConfig.clarity_project_id?.trim();
    if (clarityId && patterns.clarity.test(clarityId)) {
      if (!(window as any).clarity) {
        addScript({}, `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${clarityId}");`);
      }
    }

    // 5. Hotjar
    const hotjarId = analyticsConfig.hotjar_site_id?.trim();
    if (hotjarId && patterns.hotjar.test(hotjarId)) {
      if (!(window as any).hj) {
        addScript({}, `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${hotjarId},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=6');`);
      }
    }

    // 6. Custom Head Scripts
    const customScripts = analyticsConfig.custom_head_scripts?.trim();
    if (customScripts) {
      // Check if custom scripts were already injected by SSR
      if (!document.querySelector('[data-microsite-custom-scripts]')) {
        const wrapper = document.createElement('div');
        wrapper.setAttribute('data-microsite-custom-scripts', 'true');
        wrapper.innerHTML = customScripts;
        // Move script elements to head so they execute
        const scripts = wrapper.querySelectorAll('script');
        scripts.forEach((origScript) => {
          const newScript = document.createElement('script');
          Array.from(origScript.attributes).forEach((attr) =>
            newScript.setAttribute(attr.name, attr.value)
          );
          if (origScript.textContent) newScript.textContent = origScript.textContent;
          document.head.appendChild(newScript);
          elements.push(newScript);
        });
        // Move non-script elements to head
        const nonScripts = wrapper.querySelectorAll(':not(script)');
        nonScripts.forEach((el) => {
          document.head.appendChild(el);
          elements.push(el as HTMLElement);
        });
      }
    }

    injectedElements.current = elements;

    return () => {
      // Cleanup injected elements on unmount
      injectedElements.current.forEach((el) => {
        el.parentNode?.removeChild(el);
      });
      injectedElements.current = [];
    };
  }, [analyticsConfig, editMode]);
}
